import { useCallback, useEffect, useMemo } from "react";
import { FormikProps } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../../redux/store";
import {
  useGetLeadCitiesQuery,
  useGetLeadCountriesQuery,
} from "../../../../../../../../../redux/services/communicationApi";
import { useGetPropertiesListQuery } from "../../../../../../../../../redux/services/playgroundApi";
import MultiSelectSearchableDropdown from "../../../../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import FlagIconCDN from "../../../../../../../../../components/FlagIconCDN/FlagIconCDN";
import SearchIcon from "../../../../../../../../../assets/svg/search-icon";
import LocationIcon from "../../../../../../../../../assets/svg/location-icon";
import LocationPinIcon from "../../../../../../../../../assets/svg/location-pin-icon";
import HomeIcon from "../../../../../../../../../assets/svg/home-icon";
import CrossIcon from "../../../../../../../../../assets/svg/cross-icon";
import { CountryOption } from "../../AgentDetails/AgentDetailAccordions/utils/types";
import { AssignRoleValues } from "../../../../utils/types";
import { EditUserDetailsValues } from "../../../../../EditUserDrawer/utils/types";

type ScopeFormik =
  | FormikProps<AssignRoleValues>
  | FormikProps<EditUserDetailsValues>;

interface ScopeSectionProps {
  label: string;
  options: CountryOption[];
  values: string[];
  placeholder: string;
  searchPlaceholder: string;
  isLoading?: boolean;
  disabled?: boolean;
  isFlagIcon?: boolean;
  Icon?: React.ElementType;
  iconClassName?: string;
  onChange: (values: string[]) => void;
  onRemove: (id: string) => void;
}

const ScopeSection = ({
  label,
  options,
  values,
  placeholder,
  searchPlaceholder,
  isLoading,
  disabled,
  isFlagIcon,
  Icon,
  iconClassName,
  onChange,
  onRemove,
}: ScopeSectionProps) => {
  const selectedOptions = values
    ?.map((id) => options?.find((option) => option?.value === id))
    ?.filter((option): option is CountryOption => Boolean(option));

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-1">
        <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk leading-4">
          {label}
        </span>
        {values?.length > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center border border-gray-100 dark:border-gray-100-dk text-verba-indigo dark:text-verba-indigo-dk text-xxs font-semibold rounded-[4px]">
            {values.length}
          </span>
        )}
      </div>

      <MultiSelectSearchableDropdown
        options={isLoading ? [] : options}
        value={values}
        onChange={onChange}
        disabled={disabled}
        placeholder={isLoading ? "Loading..." : placeholder}
        searchPlaceholder={searchPlaceholder}
        showSearch={true}
        className="w-[264px]"
        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
        searchInputClassName="!border-b-0 text-gray-placeholder dark:text-gray-placeholder-dk text-md gap-1.5 !px-2"
        triggerClassName="!rounded-lg !bg-pure-white dark:!bg-pure-white-dk h-9"
        optionsListClassName="!py-0"
        searchIconClassName="w-[18px] h-[18px]"
        optionClassName="text-heading-text dark:text-heading-text-dk text-md font-normal !px-2 border-none"
        selectedOptionClassName="!bg-card-highlight-2 !dark:bg-card-highlight-2-dk"
        inputContainerClassName="!px-2 !py-[9px]"
        triggerIconClassName="!w-[10px] !h-[10px]"
        Icon={SearchIcon}
        iconClassName="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk"
        showSelectedOptionsChips={false}
      />

      {selectedOptions?.length > 0 && (
        <div className="flex flex-wrap items-start gap-1.5 w-full">
          {selectedOptions.map((option) => (
            <div
              key={option.value}
              className="group h-[22px] flex items-center gap-1 pl-2 pr-2 py-[2px] rounded-md border border-gray-50 dark:border-gray-50-dk bg-hover-effect dark:bg-hover-effect-dk"
            >
              {isFlagIcon ? (
                <FlagIconCDN
                  code={option.flag || ""}
                  flagIconClassName="w-[18px] h-[14px] shrink-0 border border-gray-75 dark:border-gray-75-dk rounded-[2px]"
                />
              ) : (
                Icon && <Icon className={iconClassName} />
              )}
              <span className="text-md font-normal leading-[18px] text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                {option.content}
              </span>
              <button
                type="button"
                onClick={() => onRemove(option.value)}
                aria-label={`Remove ${option.content}`}
                className="hidden group-hover:flex items-center justify-center"
              >
                <CrossIcon className="w-3.5 h-3.5 text-verba-red-alert dark:text-verba-red-alert-dk" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface PropertyGroupSelectorProps {
  formik: ScopeFormik;
}

const getValues = (formik: ScopeFormik, field: string): string[] =>
  (formik?.values?.[field as keyof typeof formik.values] as string[]) || [];

export const PropertyGroupSelector = ({
  formik,
}: PropertyGroupSelectorProps) => {
  const managerId =
    useSelector((state: RootState) => state?.auth?.client?.client_code) || "";

  const countryValues = getValues(formik, "country_ids");
  const cityValues = getValues(formik, "city_ids");
  const propertyValues = getValues(formik, "property_ids");

  const hasPropertyScope = countryValues.length > 0 || cityValues.length > 0;

  const { data: countryData, isLoading: isCountryLoading } =
    useGetLeadCountriesQuery({});

  const { data: cityData, isLoading: isCityLoading } = useGetLeadCitiesQuery(
    countryValues.join(","),
    { skip: countryValues.length === 0 }
  );

  const { data: propertyData, isLoading: isPropertyLoading } =
    useGetPropertiesListQuery(
      {
        managerId,
        page: 1,
        pageSize: 1000,
        country: countryValues.join(","),
        city: cityValues.join(","),
      },
      { skip: !managerId || !hasPropertyScope }
    );

  const countryOptions = useMemo<CountryOption[]>(
    () =>
      countryData?.map(
        (country: { id: number | string; name: string; alpha_2: string }) => ({
          value: String(country?.id),
          label: country?.name,
          content: country?.name,
          flag: country?.alpha_2,
        })
      ) || [],
    [countryData]
  );

  const cityOptions = useMemo<CountryOption[]>(
    () =>
      cityData?.map((city: { id: number | string; name: string }) => ({
        value: String(city?.id),
        label: city?.name,
        content: city?.name,
      })) || [],
    [cityData]
  );

  const propertyOptions = useMemo<CountryOption[]>(
    () =>
      propertyData?.res?.map(
        (property: { id: number | string; name: string }) => ({
          value: String(property?.id),
          label: property?.name,
          content: property?.name,
        })
      ) || [],
    [propertyData]
  );

  const handleChange = useCallback(
    (field: string) => (values: string[]) =>
      formik?.setFieldValue(field, values || []),
    [formik]
  );

  const handleRemove = useCallback(
    (field: string) => (id: string) =>
      formik?.setFieldValue(
        field,
        getValues(formik, field).filter((value) => value !== id)
      ),
    [formik]
  );

  useEffect(() => {
    if (countryValues.length === 0) {
      if (cityValues.length > 0) {
        formik?.setFieldValue("city_ids", []);
      }
      return;
    }
    if (isCityLoading) return;
    const validCityValues = cityValues.filter((value) =>
      cityOptions.some((option) => option?.value === value)
    );
    if (validCityValues.length !== cityValues.length) {
      formik?.setFieldValue("city_ids", validCityValues);
    }
  }, [cityOptions, cityValues, countryValues.length, isCityLoading, formik]);

  useEffect(() => {
    if (!hasPropertyScope) {
      if (propertyValues.length > 0) {
        formik?.setFieldValue("property_ids", []);
      }
      return;
    }
    if (isPropertyLoading) return;
    const validPropertyValues = propertyValues.filter((value) =>
      propertyOptions.some((option) => option?.value === value)
    );
    if (validPropertyValues.length !== propertyValues.length) {
      formik?.setFieldValue("property_ids", validPropertyValues);
    }
  }, [
    propertyOptions,
    propertyValues,
    hasPropertyScope,
    isPropertyLoading,
    formik,
  ]);

  return (
    <div className="mt-8 flex flex-col gap-6">
      <ScopeSection
        label="Select Country"
        options={countryOptions}
        values={countryValues}
        placeholder="Search or select country"
        searchPlaceholder="Search or select country"
        isLoading={isCountryLoading}
        isFlagIcon={true}
        onChange={handleChange("country_ids")}
        onRemove={handleRemove("country_ids")}
      />

      <ScopeSection
        label="Select City"
        options={cityOptions}
        values={cityValues}
        placeholder={
          countryValues.length === 0
            ? "Select country first"
            : "Search or select city"
        }
        searchPlaceholder="Search or select city"
        isLoading={isCityLoading}
        disabled={countryValues.length === 0}
        Icon={LocationIcon}
        iconClassName="w-3.5 h-3.5 shrink-0 text-dark-gray dark:text-dark-gray-dk"
        onChange={handleChange("city_ids")}
        onRemove={handleRemove("city_ids")}
      />

      <ScopeSection
        label="Select Property"
        options={propertyOptions}
        values={propertyValues}
        placeholder={
          hasPropertyScope
            ? "Search or select properties"
            : "Select country or city first"
        }
        searchPlaceholder="Search or select properties"
        isLoading={isPropertyLoading}
        disabled={!hasPropertyScope}
        Icon={HomeIcon}
        iconClassName="w-3.5 h-3.5 shrink-0 text-dark-gray dark:text-dark-gray-dk"
        onChange={handleChange("property_ids")}
        onRemove={handleRemove("property_ids")}
      />
    </div>
  );
};
